import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { FitQuestion as Q } from "./questions";

interface FitQuestionProps {
  question: Q;
  stepIdx: number;
  selectedValue: string | undefined;
  echo: string | undefined;
  inverted: boolean;
  fadeColor: string;
  onSelect: (value: string) => void;
  onBack: () => void;
  onNext: () => void;
  canBack: boolean;
  canNext: boolean;
}

export function FitQuestion({
  question,
  stepIdx,
  selectedValue,
  echo,
  inverted,
  fadeColor,
  onSelect,
  onBack,
  onNext,
  canBack,
  canNext,
}: FitQuestionProps) {
  const { t } = useTranslation();
  return (
    <>
      <span className={cn("eyebrow", fadeColor, inverted ? "text-page-bg/70" : "")}>
        <span className={cn("num", fadeColor, inverted ? "text-page-bg/55" : "")}>
          0{stepIdx + 1} —
        </span>{" "}
        {question.eyebrow}
      </span>
      <h1
        className={cn(
          "mt-4 text-[clamp(32px,5.4vw,60px)] leading-[1.05] font-medium tracking-[-0.035em]",
          fadeColor,
          inverted ? "text-page-bg" : "text-ink",
        )}
      >
        {question.title}
      </h1>
      <p
        className={cn(
          "mx-auto mt-4 max-w-[56ch] text-[clamp(14px,1.3vw,16px)] leading-[1.55]",
          fadeColor,
          inverted ? "text-page-bg/75" : "text-ink-2",
        )}
      >
        {question.intro}
      </p>

      <div className="mt-8 grid grid-cols-1 gap-3 text-left sm:grid-cols-2">
        {question.options.map((opt) => {
          const isSelected = opt.value === selectedValue;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              aria-pressed={isSelected}
              className={cn(
                "rounded-2xl border px-5 py-4 text-[15px] font-medium tracking-tight backdrop-blur transition-all duration-200",
                "flex items-center justify-between gap-3",
                isSelected
                  ? inverted
                    ? "border-page-bg bg-page-bg text-ink shadow-lg shadow-black/30"
                    : "border-ink bg-ink text-page-bg shadow-lg shadow-black/15"
                  : inverted
                    ? "border-page-bg/30 bg-ink/30 text-page-bg hover:border-page-bg/50 hover:bg-ink/45"
                    : "border-ink/15 bg-surface/65 text-ink hover:border-ink/30 hover:bg-surface",
              )}
            >
              <span>{opt.label}</span>
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full transition-opacity",
                  isSelected ? "opacity-100" : "opacity-0",
                  inverted ? "bg-ink/15 text-ink" : "bg-page-bg/20 text-page-bg",
                )}
              >
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            </button>
          );
        })}
      </div>

      {/* Reinforcement microcopy — keyed on `echo` so the element re-mounts
          and replays the fade when the user picks a different option. */}
      <div className="mt-6 flex min-h-[2.4em] items-start justify-center px-4">
        {echo && (
          <p
            key={echo}
            className={cn(
              "animate-microcopy-in serif-italic max-w-[52ch] text-[clamp(14px,1.3vw,16px)] leading-[1.45]",
              fadeColor,
              inverted ? "text-page-bg/85" : "text-ink-2",
            )}
          >
            {echo}
          </p>
        )}
      </div>

      <div className="mt-8 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={!canBack}
          className={cn(
            "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[14px] font-medium tracking-tight backdrop-blur transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            inverted
              ? "border-page-bg/30 bg-ink/30 text-page-bg hover:bg-ink/40"
              : "border-ink/15 bg-surface/60 text-ink hover:bg-surface",
          )}
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
          {t("fit.back")}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          className={cn(
            "inline-flex items-center gap-2 rounded-full py-2 pr-2 pl-5 text-[14px] font-medium tracking-tight transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            inverted ? "bg-page-bg text-ink hover:bg-white" : "bg-ink text-page-bg hover:bg-[#000]",
          )}
        >
          {t("fit.continue")}
          <span
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-full",
              inverted ? "bg-ink/15" : "bg-white/15",
            )}
          >
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
        </button>
      </div>
    </>
  );
}
